import React from 'react';
import { Box, Tooltip } from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  StarHalf as StarHalfIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * Star Rating Component
 *
 * Displays a 5-star rating with half-star support
 * - Shows full stars for whole numbers
 * - Shows half stars for .5 ratings
 * - Always shows stars (empty if no rating)
 * - Supports rich tooltips with detailed rating information
 */
const StarRating = ({
  rating = 0,
  maxRating = 5,
  size = 'small',
  showTooltip = true,
  showValue = false,
  color = 'warning',
  sx = {},
  // Rating metadata for enhanced tooltip
  totalFeedbackCount = null,
  feedbackCoverage = null,
  minRating = null,
  maxRatingValue = null,
  positiveFeedbackCount = null,
  negativeFeedbackCount = null,
  totalExecutions = null,
}) => {
  // Calculate full stars, half star, and empty stars
  // Always show stars (empty if no rating)
  const safeRating = rating || 0;
  const fullStars = Math.floor(safeRating);
  const hasHalfStar = safeRating > 0 && safeRating % 1 >= 0.5;
  const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

  // Clamp rating to maxRating
  const clampedRating = Math.min(safeRating, maxRating);

  const renderStars = () => {
    const stars = [];

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <StarIcon
          key={`full-${i}`}
          fontSize={size}
          sx={{ color: `${color}.main`, ...sx }}
        />
      );
    }

    // Half star
    if (hasHalfStar) {
      stars.push(
        <StarHalfIcon
          key="half"
          fontSize={size}
          sx={{ color: `${color}.main`, ...sx }}
        />
      );
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <StarBorderIcon
          key={`empty-${i}`}
          fontSize={size}
          sx={{ color: `${color}.main`, opacity: 0.3, ...sx }}
        />
      );
    }

    return stars;
  };

  const ratingContent = (
    <Box
      display="flex"
      alignItems="center"
      gap={0.5}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        ...sx,
      }}
    >
      {renderStars()}
      {showValue && (
        <Box
          component="span"
          sx={{
            ml: 0.5,
            fontSize: '0.75rem',
            color: 'text.secondary',
            fontWeight: 'medium',
          }}
        >
          {clampedRating.toFixed(1)}
        </Box>
      )}
    </Box>
  );

  if (showTooltip) {
    // Build rich tooltip content
    const buildTooltipContent = () => {
      if (safeRating > 0) {
        const parts = [];

        // Main rating
        parts.push(`Rating: ${clampedRating.toFixed(1)} out of ${maxRating}`);

        // Add detailed information if available
        if (totalFeedbackCount !== null && totalFeedbackCount > 0) {
          parts.push(
            `\n${totalFeedbackCount} ${totalFeedbackCount === 1 ? 'review' : 'reviews'}`
          );

          // Show rating range if available
          if (
            minRating !== null &&
            maxRatingValue !== null &&
            minRating !== maxRatingValue
          ) {
            parts.push(
              `Range: ${minRating.toFixed(1)} - ${maxRatingValue.toFixed(1)}`
            );
          }

          // Show positive/negative breakdown if available
          if (
            positiveFeedbackCount !== null &&
            negativeFeedbackCount !== null
          ) {
            const positivePct =
              totalFeedbackCount > 0
                ? Math.round((positiveFeedbackCount / totalFeedbackCount) * 100)
                : 0;
            const negativePct =
              totalFeedbackCount > 0
                ? Math.round((negativeFeedbackCount / totalFeedbackCount) * 100)
                : 0;

            if (positivePct > 0 || negativePct > 0) {
              const breakdown = [];
              if (positivePct > 0)
                breakdown.push(`${positivePct}% positive (≥4⭐)`);
              if (negativePct > 0)
                breakdown.push(`${negativePct}% negative (≤2⭐)`);
              if (breakdown.length > 0) {
                parts.push(breakdown.join(', '));
              }
            }
          }

          // Show feedback coverage if available
          if (
            feedbackCoverage !== null &&
            totalExecutions !== null &&
            totalExecutions > 0
          ) {
            parts.push(
              `Coverage: ${feedbackCoverage.toFixed(1)}% of executions`
            );
          } else if (feedbackCoverage !== null) {
            parts.push(`Coverage: ${feedbackCoverage.toFixed(1)}%`);
          }
        } else if (totalExecutions !== null && totalExecutions > 0) {
          parts.push(
            `\nNo feedback yet (${totalExecutions} execution${totalExecutions !== 1 ? 's' : ''})`
          );
        } else {
          parts.push(`\nNo feedback yet`);
        }

        return parts.join('\n');
      } else {
        // No rating
        if (totalExecutions !== null && totalExecutions > 0) {
          return `No rating yet\n${totalExecutions} execution${totalExecutions !== 1 ? 's' : ''} without feedback`;
        }
        return 'No rating yet';
      }
    };

    return (
      <Tooltip
        title={
          <Box
            component="div"
            sx={{
              whiteSpace: 'pre-line',
              fontSize: '0.75rem',
              lineHeight: 1.5,
            }}
          >
            {buildTooltipContent()}
          </Box>
        }
        arrow
        placement="top"
      >
        {ratingContent}
      </Tooltip>
    );
  }

  return ratingContent;
};

StarRating.propTypes = {
  rating: PropTypes.number,
  maxRating: PropTypes.number,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showTooltip: PropTypes.bool,
  showValue: PropTypes.bool,
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'warning',
    'error',
    'info',
    'success',
  ]),
  sx: PropTypes.object,
  // Rating metadata for enhanced tooltip
  totalFeedbackCount: PropTypes.number,
  feedbackCoverage: PropTypes.number,
  minRating: PropTypes.number,
  maxRatingValue: PropTypes.number,
  positiveFeedbackCount: PropTypes.number,
  negativeFeedbackCount: PropTypes.number,
  totalExecutions: PropTypes.number,
};

export default StarRating;
